import { useCallback, useEffect, useState } from 'react';
import { App, Button, Descriptions, Input, InputNumber, Modal, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { getAssistSettings, setAssistBudget, setAssistKillSwitch } from '@/api/caseAssist';
import type { AssistControlChange, AssistSettings } from '@/api/caseAssist';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import { EmptyState } from '@/components/EmptyState';
import { formatAbsolute } from '@/lib/time';

const { Title, Paragraph, Text } = Typography;
const MICROS = 1_000_000;

/** A stored before or after, in words. Anything it cannot read is shown as it is, rather than hidden. */
export function controlValueText(value: string): string {
  try {
    const v = JSON.parse(value) as { killed?: boolean; monthlyTokens?: number; monthlyCostMicrosUsd?: number };
    if (typeof v.killed === 'boolean') return v.killed ? 'Model switched off' : 'Not engaged';
    if (typeof v.monthlyTokens === 'number' && typeof v.monthlyCostMicrosUsd === 'number') {
      return `${v.monthlyTokens.toLocaleString()} tokens, $${(v.monthlyCostMicrosUsd / MICROS).toFixed(2)}`;
    }
  } catch {
    // Falls through to the stored text.
  }
  return value;
}

const CHANGE_COLUMNS: ColumnsType<AssistControlChange> = [
  { title: 'Control', dataIndex: 'control', width: 120, render: (v: string) => (v === 'KILL_SWITCH' ? 'Kill switch' : 'Budget') },
  { title: 'Before', key: 'before', render: (_, c) => controlValueText(c.before) },
  { title: 'After', key: 'after', render: (_, c) => controlValueText(c.after) },
  { title: 'Why', dataIndex: 'reason' },
  { title: 'By', dataIndex: 'actor', width: 190 },
  { title: 'When', key: 'at', width: 190, render: (_, c) => formatAbsolute(c.at) },
];

type Pending = { what: 'kill'; killed: boolean } | { what: 'budget'; tokens: number; costMicros: number };

/** The hub's controls for case assistance (OL-31, ADR-006). Every change asks why and lands in the list below. */
export function CaseAssistSettingsPanel() {
  const { message } = App.useApp();
  const [settings, setSettings] = useState<AssistSettings | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [tokens, setTokens] = useState<number | null>(null);
  const [usd, setUsd] = useState<number | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const apply = useCallback((s: AssistSettings) => {
    setSettings(s);
    setTokens(s.monthlyTokens);
    setUsd(s.monthlyCostMicrosUsd / MICROS);
  }, []);

  useEffect(() => {
    getAssistSettings().then(apply).catch((e) => {
      const err = normalizeApiError(e);
      if (isNotAvailable(err) || err.httpStatus === 403) setUnavailable(true);
      else message.error(err.message);
    });
  }, [apply, message]);

  const save = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      apply(pending.what === 'kill' ? await setAssistKillSwitch(pending.killed, reason) : await setAssistBudget(pending.tokens, pending.costMicros, reason));
      message.success('Saved and recorded.');
      setPending(null);
      setReason('');
    } catch (e) {
      message.error(normalizeApiError(e).message);
    } finally {
      setSaving(false);
    }
  };

  if (unavailable) {
    return <EmptyState compact title="Assistance controls are not available" description="Your role cannot see these controls, or this deployment does not serve them." />;
  }
  if (!settings) return null;
  return (
    <div style={{ maxWidth: 900, display: 'flex', flexDirection: 'column', gap: 16 }} data-testid="assist-settings">
      <div>
        <Title level={5} style={{ margin: 0 }}>Case assistance</Title>
        <Paragraph type="secondary" style={{ margin: '4px 0 0', fontSize: 13 }}>
          Suggestions on a case: a summary, questionnaire follow-up analysis and where to look first. They only propose. Without a model they
          are built from the case record. The kill switch and the budget apply to model calls; a case page works whatever you set here.
        </Paragraph>
      </div>
      <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label="Model">{settings.modelConfigured ? 'Connected' : 'None connected: suggestions are built from the case record'}</Descriptions.Item>
        <Descriptions.Item label="Kill switch">
          <Space wrap>
            <Tag bordered={false} color={settings.killed ? 'error' : 'success'} data-testid="assist-kill-state">{settings.killed ? 'Model switched off' : 'Not engaged'}</Tag>
            {settings.killed && settings.killedBy && <Text style={{ fontSize: 12 }}>{settings.killedBy}{settings.killedAt ? `, ${formatAbsolute(settings.killedAt)}` : ''}{settings.killReason ? `: ${settings.killReason}` : ''}</Text>}
            <Button size="small" danger={!settings.killed} onClick={() => setPending({ what: 'kill', killed: !settings.killed })} data-testid="assist-kill-toggle">
              {settings.killed ? 'Switch back on' : 'Switch the model off'}
            </Button>
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="Spent this month">
          <span data-testid="assist-spent">{settings.spentTokens.toLocaleString()} tokens · ${(settings.spentCostMicrosUsd / MICROS).toFixed(2)}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Monthly budget">
          <Space wrap>
            <InputNumber min={0} value={tokens} onChange={(v) => setTokens(v)} addonAfter="tokens" aria-label="Monthly token budget" data-testid="assist-budget-tokens" />
            <InputNumber min={0} value={usd} onChange={(v) => setUsd(v)} addonBefore="$" aria-label="Monthly cost budget in US dollars" data-testid="assist-budget-usd" />
            <Button size="small" disabled={tokens === null || usd === null} onClick={() => setPending({ what: 'budget', tokens: tokens ?? 0, costMicros: Math.round((usd ?? 0) * MICROS) })} data-testid="assist-budget-save">Save budget</Button>
          </Space>
        </Descriptions.Item>
      </Descriptions>
      {settings.changes.length > 0 && (
        <Table<AssistControlChange> size="small" rowKey={(c) => c.at + c.control} pagination={false} columns={CHANGE_COLUMNS} dataSource={settings.changes} data-testid="assist-changes" />
      )}
      <Modal
        open={pending !== null}
        title={pending?.what === 'kill' ? (pending.killed ? 'Switch the model off' : 'Switch the model back on') : 'Change the budget'}
        okText="Save"
        okButtonProps={{ disabled: reason.trim().length < 3, loading: saving, 'data-testid': 'assist-control-save' } as never}
        onOk={() => void save()}
        onCancel={() => setPending(null)}
      >
        <Paragraph style={{ fontSize: 13 }}>This is recorded with your name, the time and the state before it.</Paragraph>
        <Input.TextArea rows={3} maxLength={500} placeholder="Why" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="assist-control-reason" aria-label="Why" />
      </Modal>
    </div>
  );
}
